/**
 * Collapse CSS for carousels the builder recognizes but deliberately does not
 * re-animate. Shipping a slider runtime is out of scope; the honest interim is a
 * correct resting state — the first real slide visible, the rest hidden, and the
 * now-dead controls out of the page and out of the tab order.
 *
 * Keyed entirely on the `data-pb-motion-static` stamp the parser writes, so this
 * is one stylesheet shared by preview and export with no per-section selectors
 * and no chance of the two surfaces diverging.
 *
 * Deliberately uses no `!important`: every rule below is at least (0,3,0)
 * specificity, which outranks Swiper's own (0,1,0)/(0,2,0) rules. The golden
 * export test caps total `!important` across shipped CSS at 20, and a collapse
 * that spent that budget would be a tax on every export that has a carousel.
 *
 * The inert rule repeats its attribute selector to reach (0,3,0) rather than
 * relying on source order. It previously read as a plain two-attribute selector
 * — (0,2,0), not the (0,3,0) claimed above — which merely tied with theme rules
 * like `.product-images-container .swiper-pagination{display:flex}` and left the
 * outcome to whichever stylesheet happened to come last.
 *
 * Emission is gated on a section actually carrying the stamp, for the same
 * reason — an export with no carousel ships none of this.
 */

import { MOTION_PATTERNS } from './motion-patterns.js';

const STATIC_PATTERNS = MOTION_PATTERNS.filter(
  (pattern) => pattern.support === 'static' && pattern.stamp
);

/** The `data-pb-motion-static` values the registry can stamp. */
export function motionStaticStampValues() {
  return STATIC_PATTERNS.filter((p) => p.stamp.name === 'data-pb-motion-static').map(
    (p) => p.stamp.value
  );
}

/**
 * Track/slide shapes per stamp value. Exported for the M8 corpus gate, which
 * asserts the :first-child contract these rules depend on (M3 verified every
 * track's first element child is a slide; the gate keeps that true).
 */
export const MOTION_COLLAPSE_SHAPES = {
  swiper: {
    track: '.swiper-wrapper',
    slide: '.swiper-slide'
  },
  splide: {
    track: '.splide__list',
    slide: '.splide__slide'
  }
};

const COLLAPSE_RULES = MOTION_COLLAPSE_SHAPES;

function collapseBlockFor(value) {
  const shape = COLLAPSE_RULES[value];
  if (!shape) return '';
  const host = `[data-pb-motion-static="${value}"]`;
  return `
${host} ${shape.track} {
  transform: none;
  display: block;
  height: auto;
}

${host} ${shape.track} > ${shape.slide} {
  display: none;
}

${host} ${shape.track} > ${shape.slide}:first-child {
  display: block;
  opacity: 1;
  width: auto;
}`;
}

/**
 * Controls the parser marked inert. They are already `aria-hidden` and out of
 * the tab order; this removes them from the layout so a dead arrow is not
 * sitting over the one slide that does render.
 */
const INERT_RULE = `
[data-pb-motion-static] [data-pb-motion-inert][data-pb-motion-inert] {
  display: none;
}`;

const WORD_ROTATOR_PATTERN = MOTION_PATTERNS.find(
  (pattern) => pattern.staticFallback?.kind === 'word-rotator'
);

function requireRegistryValue(value, label) {
  if (value == null || value === '') {
    throw new Error(`word-rotator registry is missing ${label}`);
  }
  return value;
}

function markerAt(pattern, index, label) {
  return requireRegistryValue(pattern?.markers?.[index], label);
}

function relativeMarker(hostMarker, targetMarker, label) {
  const prefix = `${hostMarker} `;
  if (!targetMarker.startsWith(prefix)) {
    throw new Error(`word-rotator ${label} must descend from its host marker`);
  }
  return targetMarker.slice(hostMarker.length);
}

function attributeSelector(name, value = null) {
  if (!/^[a-zA-Z_][\w:.-]*$/.test(name)) {
    throw new Error(`word-rotator registry has an invalid attribute name: ${name}`);
  }
  if (value == null) return `[${name}]`;
  return `[${name}="${String(value).replaceAll('"', '\\"')}"]`;
}

const WORD_ROTATOR_FALLBACK = requireRegistryValue(
  WORD_ROTATOR_PATTERN?.staticFallback,
  'staticFallback'
);
const WORD_ROTATOR_HOST_MARKER = markerAt(
  WORD_ROTATOR_PATTERN,
  WORD_ROTATOR_FALLBACK.hostMarker,
  'host marker'
);
const WORD_ROTATOR_WORD_SUFFIX = relativeMarker(
  WORD_ROTATOR_HOST_MARKER,
  markerAt(WORD_ROTATOR_PATTERN, WORD_ROTATOR_FALLBACK.wordMarker, 'word marker'),
  'word marker'
);
const WORD_ROTATOR_LETTER_SUFFIX = relativeMarker(
  WORD_ROTATOR_HOST_MARKER,
  markerAt(WORD_ROTATOR_PATTERN, WORD_ROTATOR_FALLBACK.letterMarker, 'letter marker'),
  'letter marker'
);
if (!WORD_ROTATOR_LETTER_SUFFIX.startsWith(WORD_ROTATOR_WORD_SUFFIX)) {
  throw new Error('word-rotator letter marker must descend from its word marker');
}

const WORD_ROTATOR_PRESERVE = requireRegistryValue(
  WORD_ROTATOR_PATTERN?.preserve?.[WORD_ROTATOR_FALLBACK.preserve],
  'width preservation'
);
if (
  WORD_ROTATOR_PRESERVE.marker !== WORD_ROTATOR_FALLBACK.hostMarker ||
  WORD_ROTATOR_PRESERVE.property !== 'width'
) {
  throw new Error('word-rotator fallback width must be preserved from its host marker');
}
if (WORD_ROTATOR_PATTERN.stamp?.on !== WORD_ROTATOR_HOST_MARKER) {
  throw new Error('word-rotator stamp host must match its registry host marker');
}

const WORD_ROTATOR_STAMP_SELECTOR = attributeSelector(
  requireRegistryValue(WORD_ROTATOR_PATTERN.stamp?.name, 'stamp name'),
  requireRegistryValue(WORD_ROTATOR_PATTERN.stamp?.value, 'stamp value')
);

/** Attribute set by the IF-4 driver when it owns a rotator. */
export const WORD_ROTATOR_ARMED_ATTR = requireRegistryValue(
  WORD_ROTATOR_FALLBACK.armedAttribute,
  'armed attribute'
);

/** Builder-owned copy of the captured wrapper width. */
export const WORD_ROTATOR_FALLBACK_WIDTH_VAR = requireRegistryValue(
  WORD_ROTATOR_PRESERVE.as,
  'fallback width variable'
);

const WORD_ROTATOR_RUNTIME_ATTR = requireRegistryValue(
  WORD_ROTATOR_FALLBACK.runtimeHandshakeAttribute,
  'runtime handshake attribute'
);
/**
 * The word-list attribute, DERIVED from the host marker rather than declared a
 * second time. `.rotating-text-wrapper[data-words]` is the registry's statement
 * that a rotator is a wrapper carrying a word list; a private copy of the name
 * in the runtime could drift from the selector that decides what gets stamped.
 */
const WORD_ROTATOR_WORDS_ATTR = (() => {
  const attrs = WORD_ROTATOR_HOST_MARKER.match(/\[([a-zA-Z_][\w:.-]*)\]/g) || [];
  if (attrs.length !== 1) {
    throw new Error('word-rotator host marker must carry exactly one bare attribute (the words)');
  }
  return attrs[0].slice(1, -1);
})();

/**
 * Everything the IF-4 runtime driver needs, derived from the one registry
 * entry. Exported as a frozen object so the driver imports the contract instead
 * of restating selectors that would then be free to drift.
 */
export const WORD_ROTATOR_RUNTIME_CONTRACT = Object.freeze({
  hostSelector: WORD_ROTATOR_STAMP_SELECTOR,
  wordSuffix: WORD_ROTATOR_WORD_SUFFIX,
  letterSuffix: WORD_ROTATOR_LETTER_SUFFIX,
  armedAttr: WORD_ROTATOR_ARMED_ATTR,
  runtimeAttr: WORD_ROTATOR_RUNTIME_ATTR,
  widthVar: WORD_ROTATOR_FALLBACK_WIDTH_VAR,
  wordsAttr: WORD_ROTATOR_WORDS_ATTR
});

const WORD_ROTATOR_UNARMED_HOST = `${WORD_ROTATOR_STAMP_SELECTOR}:not(${attributeSelector(
  WORD_ROTATOR_ARMED_ATTR
)})`;
const WORD_ROTATOR_STALE_ARMED_HOST = `html:not(${attributeSelector(
  WORD_ROTATOR_RUNTIME_ATTR
)}) ${WORD_ROTATOR_STAMP_SELECTOR}${attributeSelector(WORD_ROTATOR_ARMED_ATTR)}`;

function wordRotatorFallbackSelectors(suffix = '') {
  return `${WORD_ROTATOR_UNARMED_HOST}${suffix},\n${WORD_ROTATOR_STALE_ARMED_HOST}${suffix}`;
}

/**
 * Static word-rotator fallback, generated entirely from the registry entry.
 * The source word remains absolutely positioned; the preserved measurement is
 * what gives its wrapper the same load-bearing layout box as the crawl. The
 * fallback stays active until BOTH the runtime handshake and per-element armed
 * attribute exist, so stale markup can never hide a word without a driver.
 */
const WORD_ROTATOR_FALLBACK_RULE = `
${wordRotatorFallbackSelectors()} {
  width: var(${WORD_ROTATOR_FALLBACK_WIDTH_VAR});
}
${wordRotatorFallbackSelectors(WORD_ROTATOR_LETTER_SUFFIX)} {
  opacity: 1;
  transform: none;
  transition: none;
}`;

const MOTION_STATIC_CSS = `${motionStaticStampValues()
  .map(collapseBlockFor)
  .filter(Boolean)
  .join('\n')}\n${INERT_RULE}`.trim();

function htmlCarriesStamp(html, selector, substring) {
  const source = String(html || '');
  if (!source) return false;

  if (typeof DOMParser !== 'undefined') {
    try {
      const doc = new DOMParser().parseFromString(`<div>${source}</div>`, 'text/html');
      return !!doc.querySelector(selector);
    } catch {
      // Fall through to the source check below.
    }
  }
  // Substring fallback for DOMParser-less contexts (Node imports of the export
  // generators; a parse that threw). Deliberately fails OPEN, which is why the
  // repo's no-string-HTML-detection rule is set aside here: a false positive
  // emits an attribute-keyed stylesheet that matches nothing, while a false
  // negative renders a stamped carousel with every slide stacked. The browser
  // surfaces that actually ship this CSS always take the DOMParser branch.
  return source.includes(substring);
}

/**
 * @param {Array<{html?: string}>} sections
 * @returns {string} the collapse stylesheet — carousel block when a
 *   data-pb-motion-static stamp is present, the word-rotator fallback when its
 *   stamp is present, both when both — or '' when no section carries either.
 */
export function assembleMotionStaticCSS(sections) {
  if (!Array.isArray(sections) || sections.length === 0) return '';
  const parts = [];
  if (
    sections.some((section) =>
      htmlCarriesStamp(section?.html, '[data-pb-motion-static]', 'data-pb-motion-static')
    )
  ) {
    parts.push(MOTION_STATIC_CSS);
  }
  if (
    sections.some((section) =>
      htmlCarriesStamp(
        section?.html,
        WORD_ROTATOR_STAMP_SELECTOR,
        `${WORD_ROTATOR_PATTERN.stamp.name}="${WORD_ROTATOR_PATTERN.stamp.value}"`
      )
    )
  ) {
    parts.push(WORD_ROTATOR_FALLBACK_RULE.trim());
  }
  return parts.join('\n');
}
